import {api} from "@/shared/api";
import {AssignmentPanel} from "@/shared/admin/assignmentPanel";
import {FormUtil} from "@/shared/forms/formUtil";
import * as EntityLinks from "@/shared/entityLinks";
import {htmlEscape, stringNotBlank} from "@/shared/utils/string";

import {LocationPanel} from "@/shared/locationPanel";
import {TaskAdministrationDialog} from "./taskAdministrationDialog";
import {UserAssignmentPanel} from "@/shared/admin/basicSameTypeAssignmentPanel";
import {AttachmentGallery} from "@/shared/ui/attachmentGallery";
import {BudgetCalculator, ConsumedBudgetResult} from "@/shared/budget/budgetCalculator";
import {promiseNow} from "@/shared/utils/promise";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {debounce, hoursToMillis} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDate, formatDuration, formatTime, parseISODate, parseISOTime} from "@/shared/utils/datefunctions";
import {
	AssignedUser,
	CombinedRecordingStatus,
	PartialUser,
	ProjectTimeStatus,
	Task,
	TaskAttachmentDownloadUrlResponse,
	TaskDetailsResponse,
	TaskUserBudgetInfo
} from "@/shared/timr";
import {WorkingTimeTypeCategory} from "@/shared/timrTypes";

export interface TaskInfoDialogConfig {
	dialogId: string;
	size?: BootstrapModal.ModalSize;
	onQuickSwitch?: (task: Task) => void; // used to switch or start a recording with this task (only required if recording should be enabled)
	recordingStatus?: ProjectTimeStatus | CombinedRecordingStatus; // used to update the quick switch/start recording buttons (only required if recording should be enabled)
}

export class TaskInfoDialog {
	private readonly dom: JQuery;
	private readonly recordingButtonsVisible: boolean = false;

	private dialog: BootstrapModal.Modal;
	private leaderAssignmentPanel?: AssignmentPanel<AssignedUser, PartialUser>;
	private isTaskLeader = false;
	private recordingStatus: ProjectTimeStatus | CombinedRecordingStatus;
	private taskDetailsResponse: TaskDetailsResponse;
	private userId: number;
	private taskAdministrationDialog: TaskAdministrationDialog;

	private locationPanel?: LocationPanel;

	private attachmentsLoaded: boolean;
	private readonly attachmentGallery?: AttachmentGallery

	constructor(config: TaskInfoDialogConfig) {
		this.dom = $(config.dialogId);
		this.recordingButtonsVisible = config.onQuickSwitch != null;
		this.recordingStatus = config.recordingStatus ?? null;

		this.initDialog(config);
		if (SecurityStatus.isCorporateFeaturesAllowed()) {
			this.initTaskProjectLeaders();
		}
		this.locationPanel = document.getElementById("taskInfoLocationPanel") as LocationPanel;
		this.locationPanel?.setPositionSectionVisible(false);
		this.locationPanel?.setReadonly(true);

		// Resize assignment panels on window size change
		$(window).resize(debounce(() => this.resizeAssignmentPanels(), this, 50));

		$('a[data-toggle="tab"]').on("shown.bs.tab", (e) => {
			const tabId = $(e.target).attr("href");
			if (tabId === "#taskInfoProjectLeaders") {
				this.leaderAssignmentPanel?.updateView();
				this.leaderAssignmentPanel?.reloadAssigned();
			} else if (tabId === "#taskInfoPosition") {
				this.locationPanel?.refreshMap();
			} else if (tabId === "#taskAttachments") {
				this.updateAttachmentsTab();
			}
		});

		this.attachmentGallery = document.getElementById("taskAttachmentGallery") as AttachmentGallery
	}

	protected initDialog(config: TaskInfoDialogConfig) {
		this.dialog = new BootstrapModal.Modal({
			size: config.size || "modal-xl",
			title: "",
			escapeText: false,
			message: this.dom,
			buttons: [{
				text: "dialog.close",
				action: () => {
					this.closeDialog();
				},
				css: "closeButton btn-primary"
			}, {
				text: "dialog.task.edit",
				action: () => {
					this.getTaskAdministrationDialog().edit(this.taskDetailsResponse);
					this.closeDialog();
				},
				css: "editButton btn-default",
				left: true
			},
				{
					text: "recording.projectTime.startWithTask",
					icon: "far fa-play position-left text-primary",
					visible: this.recordingButtonsVisible,
					action: () => {
						config.onQuickSwitch(this.taskDetailsResponse.task);
						this.closeDialog();
					},
					css: "startButton btn-default",
					left: true
				},
				{
					text: "recording.projectTime.switchToTask",
					icon: "far fa-exchange position-left text-primary",
					visible: this.recordingButtonsVisible,
					action: () => {
						config.onQuickSwitch(this.taskDetailsResponse.task);
						this.closeDialog();
					},
					css: "switchButton btn-default",
					left: true
				},
			],
			onOpen: () => {
				this.resizeAssignmentPanels();
			},
			onClose: () => {
				FormUtil.switchTabNavigationToFirst($("ul.dialog-nav-tabs"));
			},
			preventCloseIfLightboxOpen: true
		});
	}

	/**
	 * Updates the recording status and refreshes the recording buttons to reflect the updated state.
	 *
	 * @param recordingStatus - The new recording status to be set. Can either be a `ProjectTimeStatus` or `CombinedRecordingStatus`.
	 */
	public updateRecordingStatus(recordingStatus: ProjectTimeStatus | CombinedRecordingStatus): void {
		this.recordingStatus = recordingStatus;
		this.updateRecordingButtons();
	}

	/**
	 * Updates the visibility, title, and enabled/disabled state of recording-related buttons (start and switch button)
	 * This is based on the current task details, recording status (varies depending on whether project time or combined recording).
	 */
	private updateRecordingButtons(): void {
		// If the dialog object has already been created, the load task details request is still in flight, and the
		// recording page updates the status at the same time then the update call would cause a js error as the response
		// is not yet set in the instance field.

		// basic condition for recording is that task is enabled and bookable; if task dialog is opened in combined recording, (attendance) working time needs to be running
		const isTaskRecordable = this.taskDetailsResponse && this.taskDetailsResponse?.task.bookable && !this.taskDetailsResponse.task.disabled &&
			(!this.isCombinedRecordingStatus(this.recordingStatus) || this.isTaskStartOrSwitchAllowedInCombinedRecording(this.recordingStatus));

		let title = "";
		if (!(this.taskDetailsResponse?.task.bookable)) {
			title = messages["recording.projectTime.taskInfo.notBookable"];
		} else if (this.taskDetailsResponse?.task.disabled) {
			title = messages["recording.projectTime.taskInfo.notAvailable"];
		} else if (this.isCombinedRecordingStatus(this.recordingStatus) && !this.isTaskStartOrSwitchAllowedInCombinedRecording(this.recordingStatus)) {
			title = messages[`recording.projectTime.taskInfo.noAttendanceTimeRunning.${this.isProjectTimeRunning() ? "switch" : "start"}`];
		}

		// Button visibility is toggled based on the current state (so only one button is shown at a time) and if an action
		// callback is defined (for recording, not reports). Buttons are disabled based on if it's possible to record on
		// the task or not. This way the user has a clear indication of why it's not possible to start a recording.
		this.dialog.getButtons("startButton")
			.toggle(this.recordingButtonsVisible && !this.isProjectTimeRunning())
			.prop("title", title)
			.prop("disabled", !isTaskRecordable);

		this.dialog.getButtons("switchButton")
			.toggle(this.recordingButtonsVisible && this.isProjectTimeRunning())
			.prop("title", title)
			.prop("disabled", !isTaskRecordable);
	}

	private resetAttachmentsTab() {
		this.attachmentGallery?.reset();
		this.attachmentsLoaded = false;
	}

	private updateAttachmentsTab() {
		if (this.attachmentsLoaded) {
			return;
		}

		this.loadAttachments(this.taskDetailsResponse.task.id);
		this.attachmentsLoaded = true;
	}

	private loadAttachments(taskId: number) {
		if (!this.attachmentGallery) {
			return;
		}

		this.attachmentGallery.reset();

		api("tasks/getTaskAttachments.json")
			.get({
				taskId
			})
			.done((data: TaskAttachmentDownloadUrlResponse[]) => {
				this.attachmentGallery.setExistingFiles(data);
			});
	}

	public show(taskDetails: TaskDetailsResponse, userId: number) {
		this.isTaskLeader = globals.isAdmin || taskDetails.task.currentUserIsLeader && SecurityStatus.isCorporateFeaturesAllowed();

		this.dialog.setTitle(this.buildTitle(taskDetails.task));
		this.updateTaskLink(taskDetails.task);

		this.setTaskFields(taskDetails);

		this.taskDetailsResponse = taskDetails;
		this.userId = userId;

		this.resetAttachmentsTab();

		// Update assignment panel
		this.dom.find("#taskInfoLeaderNameFilter").val("");
		this.leaderAssignmentPanel?.setEditMode(false);
		this.leaderAssignmentPanel?.reloadAssigned();

		this.showDialog();
	}

	private initTaskProjectLeaders() {
		const assignedLeaders = (): JQueryPromise<AssignedUser[]> => {
			const taskId = $("#taskInfoTaskId").val() as string; // HTMLInputElement -> string

			// For a new subtask we load the parents leaders as autoAssigned users
			const loadTaskId = taskId || ($("#taskInfoParentTaskId").val() as string); // HTMLInputElement -> string

			if (loadTaskId) {
				// Partial-user endpoint: non-admins can see this dialog.
				return api("tasks/loadPartialTaskLeaders.json")
					.get({
						taskId: loadTaskId
					})
					.then((partialUsers: PartialUser[]) =>
						partialUsers.map(user => ({...user, autoAssigned: !taskId})) as unknown as AssignedUser[]
					);
			} else {
				return promiseNow<AssignedUser[]>([]);
			}
		};

		this.leaderAssignmentPanel = new UserAssignmentPanel({
			panelElement: $("#taskInfoLeaderAssignmentPanel"),
			selectSourceProvider: () => promiseNow([]),
			assignedSourceProvider: assignedLeaders,
			editMessage: "admin.task.assign.leader"
		});
		this.leaderAssignmentPanel?.setEditMode(false);
		this.leaderAssignmentPanel?.reloadAssigned();
	}

	protected setTaskFields(taskDetails: TaskDetailsResponse) {
		// get the task from the response
		const {task} = taskDetails;
		this.dom.find("#taskInfoTaskId").val(task.id);
		this.dom.find("#taskInfoParentTaskId").val(task.parentTask?.id);
		this.dom.find("#taskInfoBookable").text(task.bookable ? messages["confirmation.yes"] : messages["confirmation.no"]);
		this.dom.find("#taskInfoBillable").text(task.billable ? messages["confirmation.yes"] : messages["confirmation.no"]);
		this.dom.find("#taskInfoEarliestStartTime")
			.text(formatTime(parseISOTime(task.earliestStartTime)))
			.toggle(task.earliestStartTime !== null);
		this.dom.find("#taskInfoEarliestStartTimeLabel").toggle(task.earliestStartTime !== null);
		this.dom.find("#taskInfoLatestEndTime")
			.text(formatTime(parseISOTime(task.latestEndTime)))
			.toggle(task.latestEndTime !== null);
		this.dom.find("#taskInfoLatestEndTimeLabel").toggle(task.latestEndTime !== null);
		this.dom.find("#taskInfoDescriptionRequired").text(task.descriptionRequired ? messages["confirmation.yes"] : messages["confirmation.no"]);

		const taskInfoCustomFields = this.dom.find("#taskInfoCustomFields");
		if (task.customField1 || task.customField2 || task.customField3) {
			this.dom.find("#customTaskInfoField1").text(task.customField1 || "\xa0");
			this.dom.find("#customTaskInfoField2").text(task.customField2 || "\xa0");
			this.dom.find("#customTaskInfoField3").text(task.customField3 || "\xa0");
			taskInfoCustomFields.show();
		} else {
			taskInfoCustomFields.hide();
		}

		this.dom.find("#taskInfoDescription").text(task.description);
		this.dom.find("#taskInfoComment").toggle(stringNotBlank(task.description));

		const start = parseISODate(task.start);
		const end = parseISODate(task.end);
		this.dom.find("#taskInfoAvailability")
			.text(`${start ? messages["report.timespan.from"] + " " + formatDate(start) : ""} ${end ? messages["report.timespan.to"] + " " + formatDate(end) : ""}`)
			.toggle(start !== null || end !== null);
		this.dom.find("#taskInfoAvailabilityLabel").toggle(start !== null || end !== null);

		// Show parent task location if this task inherits its location and the parent has a valid location/address
		const parentLocationOrAddressFilled = task.parentTask != null
			&& (task.parentTask.latitude != null || task.parentTask.longitude != null || stringNotBlank(task.parentTask.address)
				|| stringNotBlank(task.parentTask.city) || stringNotBlank(task.parentTask.state)
				|| stringNotBlank(task.parentTask.zipCode) || task.parentTask.countryId != null);
		const inheritedAndValid = task.locationInherited && parentLocationOrAddressFilled;
		const locationSource = inheritedAndValid ? task.parentTask : task;
		this.locationPanel?.setData({
			latitude: locationSource.latitude,
			longitude: locationSource.longitude,
			address: locationSource.address,
			city: locationSource.city,
			zipCode: locationSource.zipCode,
			state: locationSource.state,
			countryName: locationSource.countryName,
			locationRestrictionRadius: task.locationRestrictionRadius,
		});

		if (this.dom.find("#taskInfoBudget").length) {
			void api("recording/getTaskUserBudgetInfo.json")
				.get({taskId: task.id, userId: this.userId})
				.done(data => this.updateBudgetInfo(data.taskUserBudgetInfo));
		}
	}

	/**
	 * Updates the budget information that contains the bar and the text about the current budget and the visibility of the info panel
	 * For the bar, the data of the budget is first prepared and the length of the individual elements is adjusted afterwards
	 * If there are no budget hours planned, the budget container projectTimeTaskBudget will be hided
	 * @param taskUserBudgetInfo budget data
	 * @private
	 */
	private updateBudgetInfo(taskUserBudgetInfo: TaskUserBudgetInfo) {
		if (taskUserBudgetInfo?.hoursPlanned) {
			const consumedBudgetResult: ConsumedBudgetResult = BudgetCalculator.calculateConsumedBudget(taskUserBudgetInfo, null);
			const millisPlanned = hoursToMillis(consumedBudgetResult.hoursPlanned);

			this.dom.find("#taskInfoTaskPlanned")
				.css("width", consumedBudgetResult.plannedPercentage + "%")
				.toggle(consumedBudgetResult.plannedPercentage > 0 && consumedBudgetResult.plannedPercentage < 100);
			this.dom.find("#taskInfoTaskConsumed").css("width", consumedBudgetResult.consumedPercentage + "%");
			this.dom.find("#taskInfoTaskExceeded").css("width", consumedBudgetResult.exceededPercentage + "%")
				.css("margin-left", consumedBudgetResult.plannedPercentage + "%")
				.toggle(consumedBudgetResult.exceededPercentage > 0);
			this.dom.find("#taskInfoTaskRemaining").html(formatDuration(Math.max(0, millisPlanned - consumedBudgetResult.consumed)));
			this.dom.find("#taskInfoConsumedPercent").text(messages.withArguments("task.budgetConsumed", Math.round(consumedBudgetResult.consumed / millisPlanned * 100)));
			this.dom.find("#taskInfoBudget").show();
		} else {
			this.dom.find("#taskInfoBudget").hide();
		}
	}

	protected showDialog() {
		this.dom.find(".nav > li.active").removeClass("active in");
		this.dom.find(".tab-content > div.tab-pane.active").removeClass("active in");
		this.dom.find(".nav > li:first-child").addClass("active in");
		this.dom.find(".tab-content > div.tab-pane:first-child").addClass("active in");

		this.dialog.show();

		// Prevent flickering of edit button, do it here instead of in onOpen
		// DEVNOTE: This has to be done after the dialog is shown,
		// otherwise it might be that the domModal isn't fully loaded yet and throws an error
		this.dialog.find(".editButton").toggle(this.isTaskLeader);
		this.updateRecordingButtons();
	}

	public closeDialog() {
		this.dialog.close();
	}

	private resizeAssignmentPanels() {
		const screenHeight = $(window).height();

		// Estimated height of other dialog components + padding + margin ...
		const panelHeight = Math.max(screenHeight - 400, 500);
		this.leaderAssignmentPanel?.setScrollHeight(panelHeight);
	}

	private buildTitle(task: Task) {
		// Get task path (breadCrumbs without name and without / at the end)
		const taskPath = task.breadCrumbs.substring(0, task.breadCrumbs.length - task.name.length - 1);

		return $("<div>")
			.attr("title", task.breadCrumbs)
			.append($("<h4>")
				.addClass("modal-title overflow-ellipsis text-nowrap")
				.html(htmlEscape(task.name))
			)
			.append($("<h6>")
				.addClass("text-muted m-0 overflow-ellipsis text-nowrap")
				.text(taskPath)
			).prop("outerHTML") as string;
	}

	private updateTaskLink(task: Task) {
		this.dom.find(".task-link-copy")
			.attr("href", EntityLinks.getTaskLink(task.taskId, task.breadCrumbs))
	}

	private getTaskAdministrationDialog() {
		if (!this.taskAdministrationDialog) {
			this.taskAdministrationDialog = new TaskAdministrationDialog({
				dialogId: "#taskAdministrationDialog",
			});
		}
		return this.taskAdministrationDialog;
	}

	/**
	 * Returns true if the project time is currently running.
	 */
	private isProjectTimeRunning(): boolean {
		// nothing is running if no recording status is set
		if (!this.recordingStatus) {
			return false;
		}

		return this.isCombinedRecordingStatus(this.recordingStatus) ?
			this.recordingStatus.projectTimeStatus && this.recordingStatus.projectTimeStatus.runningProjectTime !== null :
			this.recordingStatus.runningProjectTime !== null;
	}

	/**
	 * Helper function to check if the given property <code>recordingStatus</code> is of type CombinedRecordingStatus or otherwise a ProjectTimeStatus (legacy recording).
	 * This is necessary for the task info dialog to work in combined recording as well as project time recording (legacy recording)
	 * @param status The status to check, which can be either a ProjectTimeStatus or CombinedRecordingStatus.
	 */
	private isCombinedRecordingStatus(status: ProjectTimeStatus | CombinedRecordingStatus): status is CombinedRecordingStatus {
		return (status as CombinedRecordingStatus)?.workingTimeStatus !== undefined && (status as CombinedRecordingStatus)?.projectTimeStatus !== undefined;
	}

	/**
	 * <p>Checks if the current recording status allows quick start/switch.</p>
	 * If working time is running, it must be of the category 'AttendanceTime' for quick start/switch to be allowed; if no working time is running, task switch is always allowed.
	 */
	private isTaskStartOrSwitchAllowedInCombinedRecording(combinedRecordingStatus: CombinedRecordingStatus): boolean {
		const runningWorkingTime = combinedRecordingStatus.workingTimeStatus?.runningWorkingTime;
		return combinedRecordingStatus.workingTimeStatus === null || runningWorkingTime?.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime;
	}
}

/**
 * Processes the URL parameters for task details.
 * If the URL contains a valid task ID, it will open the task info dialog by calling the provided showTaskInfoDialog function with the task ID and user ID.
 * @param showTaskInfoDialog
 */
export function processTaskDetailsUrlParams(showTaskInfoDialog: (taskId: number) => void): void {
	const urlParams = new URLSearchParams(window.location.search);

	const invalidTask = urlParams.get("invalidTask");
	const taskId = urlParams.get("viewTaskId");

	if (invalidTask) {
		BootstrapModal.error({
			message: messages["taskLink.invalid"]
		})
	} else if (taskId && Number(taskId)) {
		showTaskInfoDialog(Number(taskId));
	}

	if (invalidTask || taskId) {
		// Remove query params but keep the rest of the url
		window.history.replaceState({}, document.title, window.location.pathname);
	}
}
